import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  TextInput, Select, MultiSelect, Radio, Button,
  FileInput, Image, Container, Card, Title, Loader
} from '@mantine/core';
import { makeGetRequest, makeMultipartFormPostRequest } from '../services/httpHelper';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import { isErrorObject } from '../utils/utils';
import ErrorHandler from '../services/ErrorHandler';
import { BlogCategory } from '../types';
import { useNavigate, useParams } from 'react-router-dom';

const EditBlogPost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  

  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      title: '',
      category: '',
      content: '',
      tags: [],
      published: 'false',
      featured_image: null,
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Superscript,
      SubScript,
      Highlight,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postRes, categoriesRes] = await Promise.all([
          makeGetRequest(`blog-post/${postId}`),
          makeGetRequest('blog-post-categories')
        ]);

        if (!isErrorObject(postRes)) {
          const post = postRes.data;
          form.setValues({
            title: post.title,
            category: post.category.toString()||'',
            content: post.content,
            featured_image:post.featured_image,
            tags: post.tags || [],
            published: post.published ? 'true' : 'false',
          });
          if (editor) editor.commands.setContent(post.content);
          if (post.featured_image) setPreviewImage(`${post.featured_image}`);
        }

        if (!isErrorObject(categoriesRes)) {
          setCategories(categoriesRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [postId, editor]);


  

  const handleImageChange = (file: File | null) => {
    setFeaturedImage(file);
    setPreviewImage(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const values = form.values;
    const formData = new FormData();
    const user = JSON.parse(localStorage.getItem("user") || "{}");
  
    formData.append("user_id", user.id);
    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof typeof values;
      if (typedKey === "tags" && Array.isArray(values[typedKey])) {
        formData.append(typedKey, values[typedKey].join(","));
      } else if (typedKey === "featured_image") {
        if (featuredImage) {
          formData.append("featured_image", featuredImage);
        } // If no new image is selected, do not append
      } else {
        formData.append(typedKey, String(values[typedKey]));
      }
    });
  
    setIsSubmitting(true);
    const response = await makeMultipartFormPostRequest(`blog-post/${postId}`, formData);
    setIsSubmitting(false);
    if (isErrorObject(response)) {
      setError(ErrorHandler(response));
    } else {
      navigate(`/blog/post/${response.data}/${postId}`);
    }
  };
  

  if (isLoading) return <Loader />;

  return (
    <Container size="sm">
      <Card shadow="sm" padding="lg">
        <Title order={3}>Edit Post</Title>
        {isSubmitting && <Loader />}
        {error && <p className='red'>{error}</p>}
        <form onSubmit={handleSubmit}>
          <TextInput label="Title" {...form.getInputProps('title')} required />
          <Select label="Category" data={categories.map(cat => ({ value: cat.id.toString(), label: cat.category }))} {...form.getInputProps('category')} required />

            
          <RichTextEditor editor={editor}>
  <RichTextEditor.Toolbar>
    <RichTextEditor.Bold />
    <RichTextEditor.Italic />
    <RichTextEditor.Underline />
    <RichTextEditor.ClearFormatting />
    <RichTextEditor.Highlight />
    <RichTextEditor.Code />
    <RichTextEditor.H1 />
    <RichTextEditor.H2 />
    <RichTextEditor.H3 />
    <RichTextEditor.Blockquote />
    <RichTextEditor.Hr />
    <RichTextEditor.BulletList />
    <RichTextEditor.OrderedList />
    <RichTextEditor.AlignLeft />
    <RichTextEditor.AlignCenter />
    <RichTextEditor.AlignJustify />
    <RichTextEditor.AlignRight />
  </RichTextEditor.Toolbar>
  <RichTextEditor.Content />
</RichTextEditor>

          <MultiSelect label="Tags" data={form.values.tags.map(tag => ({ value: tag, label: tag }))} creatable {...form.getInputProps('tags')} />
          <Radio.Group label="Published" {...form.getInputProps('published')}>
            <Radio value="true" label="Yes" />
            <Radio value="false" label="No" />
          </Radio.Group>
          <FileInput label="Featured Image" onChange={handleImageChange} />
          {previewImage && <Image src={previewImage} height={200} mt="md" />}
          <Button type="submit" fullWidth mt="md">{isSubmitting ? 'Saving...' : 'Update Post'}</Button>
        </form>
      </Card>
    </Container>
  );
};

export default EditBlogPost;
